/**
 * Object-level lifecycle timeline - ONE object across EVERY visible code.
 *
 * Mirrors ``GET /admin/exceptions/object-history``. Returns the ordered
 * events for ``(fp_code, data_domain, object_id)`` with no canonical collapse,
 * so a prior ``resolved -> re_detected`` history survives. Powers the drawer's
 * Activity tab (``ActivityPanel``) and the object action line's oldest-open age.
 *
 * Event taxonomy matches tuple-history plus ``marked_fixed`` (the user pressed
 * "I've fixed all N" / per-row "Mark fixed"). Each event also carries the
 * ``code`` + ``name`` so the Activity feed can render per-code chips.
 *
 * RBAC: same gate as notes (Evo/PSC only) - the backend dependency rejects
 * FP_USER with 403; we short-circuit here so the request is never sent.
 */
import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { apiClient } from '../services/api'
import { canSeeExceptionNotes } from '../services/auth'

export type ObjectTimelineEventKind =
  | 'detected'
  | 're_detected'
  | 'blocked'
  | 'resolved'
  | 'ignored'
  | 'marked_fixed'

export interface TimelineActor {
  id: number | null
  name: string | null
  email: string | null
}

export interface ObjectTimelineEvent {
  kind: ObjectTimelineEventKind
  code: string
  name: string
  occurred_at: string
  tag_id: number
  reason: string | null
  actor: TimelineActor | null
}

// Aliases for the timeline consumer (StatusHistoryTimeline): the object event
// is a superset of the old per-code tuple event (adds code + name + the
// marked_fixed kind), so the rendering logic stays generic.
export type TimelineEvent = ObjectTimelineEvent
export type TimelineEventKind = ObjectTimelineEventKind

export interface ObjectHistoryKey {
  fp_code: string
  data_domain: string
  object_id: string
  /** Drawer's focused code. Sent so the server prefix-matches the whole product
   *  family for the composite per-outlet stock codes (timeline spans every
   *  affected outlet, independent of which one is currently representative). */
  exception_code?: string
  since_days?: number
}

interface ObjectHistoryResponse {
  events: ObjectTimelineEvent[]
  last_confirmed_at: string | null
  oldest_open_detected_at: string | null
}

export interface UseExceptionObjectHistoryResult {
  events: ObjectTimelineEvent[]
  lastConfirmedAt: string | null
  oldestOpenDetectedAt: string | null
  loading: boolean
  /** True once a fetch for the CURRENT key has finished (success OR error).
   * ``loading`` starts false and only flips true after the effect fires, so a
   * naive ``!loading`` gate races on key change - this is the gate-safe
   * signal for the drawer's render-all-at-once skeleton. A null key settles
   * immediately (nothing will ever be fetched). */
  settled: boolean
  error: string | null
  refresh: () => Promise<void>
}

export function useExceptionObjectHistory(
  key: ObjectHistoryKey | null,
): UseExceptionObjectHistoryResult {
  const { user } = useAuth()
  const gated = canSeeExceptionNotes(user)
  const [events, setEvents] = useState<ObjectTimelineEvent[]>([])
  const [lastConfirmedAt, setLastConfirmedAt] = useState<string | null>(null)
  const [oldestOpenDetectedAt, setOldestOpenDetectedAt] = useState<
    string | null
  >(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // The keyHash whose fetch last completed - settled in ``finally`` so an
  // endpoint error can never freeze a consumer's loading gate.
  const [settledKey, setSettledKey] = useState<string | null>(null)

  const keyHash = key
    ? `${key.fp_code}|${key.data_domain}|${key.object_id}|${key.exception_code ?? ''}|${key.since_days ?? ''}`
    : null

  const refresh = useCallback(async (): Promise<void> => {
    if (!gated || !key) {
      setEvents([])
      setLastConfirmedAt(null)
      setOldestOpenDetectedAt(null)
      setSettledKey(keyHash)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const res = await apiClient.get<ObjectHistoryResponse>(
        '/admin/exceptions/object-history',
        { params: key },
      )
      setEvents(res.data.events)
      setLastConfirmedAt(res.data.last_confirmed_at)
      setOldestOpenDetectedAt(res.data.oldest_open_detected_at)
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail || err?.message || 'Request failed'
      setError(String(detail))
    } finally {
      setLoading(false)
      setSettledKey(keyHash)
    }
  }, [gated, key, keyHash])

  useEffect(() => {
    void refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyHash])

  return {
    events,
    lastConfirmedAt,
    oldestOpenDetectedAt,
    loading,
    settled: settledKey === keyHash,
    error,
    refresh,
  }
}
